import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, JSX } from 'react';
import type { Palette } from '../../../../_theme/palette.js';
import { hexWithAlpha } from '../../../../_theme/palette.js';
import type { SurveyQuestion } from '../../types.js';

interface SignatureFieldProps {
  palette: Palette;
  question: SurveyQuestion;
  value: unknown;
  onChange: (value: string | null) => void;
}

const PAD_WIDTH = 600;
const PAD_HEIGHT = 200;

// Pointer-events based signature pad — works with mouse, touch, and pen
// without separate handlers. Honors `settings.penColor` so survey authors
// can use a brand-tinted ink. Restores the saved signature on mount so
// back-navigation doesn't lose the user's drawing.
export function SignatureField({ palette, question, value, onChange }: SignatureFieldProps): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);
  const [hasInk, setHasInk] = useState<boolean>(Boolean(value));

  const penColor = question.settings?.penColor || palette.text;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = penColor;
  }, [penColor]);

  // Restore prior signature image when re-mounted (back-navigation, payload
  // late-arrival, etc). Runs once.
  useEffect(() => {
    if (typeof value !== 'string' || !value) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const img = new Image();
    img.onload = () => {
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      setHasInk(true);
    };
    img.src = value;
  }, []);

  const beginStroke = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.setPointerCapture?.(event.pointerId);
    drawingRef.current = true;
    lastPointRef.current = pointFromEvent(canvas, event);
  };

  const extendStroke = (event: React.PointerEvent<HTMLCanvasElement>): void => {
    if (!drawingRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const last = lastPointRef.current;
    if (!ctx || !last) return;
    const next = pointFromEvent(canvas, event);
    ctx.beginPath();
    ctx.moveTo(last.x, last.y);
    ctx.lineTo(next.x, next.y);
    ctx.stroke();
    lastPointRef.current = next;
    if (!hasInk) setHasInk(true);
  };

  const endStroke = (): void => {
    if (!drawingRef.current) return;
    drawingRef.current = false;
    lastPointRef.current = null;
    const canvas = canvasRef.current;
    if (!canvas) return;
    onChange(canvas.toDataURL('image/png'));
  };

  const clear = (): void => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
    onChange(null);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={padWrapperStyle(palette)}>
        <canvas
          ref={canvasRef}
          width={PAD_WIDTH}
          height={PAD_HEIGHT}
          aria-label={question.label}
          style={canvasStyle()}
          onPointerDown={beginStroke}
          onPointerMove={extendStroke}
          onPointerUp={endStroke}
          onPointerLeave={endStroke}
          onPointerCancel={endStroke}
        />
        {!hasInk && <span style={hintStyle(palette)}>Sign here</span>}
      </div>
      <button
        type="button"
        onClick={clear}
        className="chirp-btn"
        style={clearButtonStyle(palette)}
        aria-label="Clear signature"
      >
        <EraserIcon color={palette.textMuted} />
        Clear
      </button>
    </div>
  );
}

function pointFromEvent(
  canvas: HTMLCanvasElement,
  event: React.PointerEvent<HTMLCanvasElement>,
): { x: number; y: number } {
  const rect = canvas.getBoundingClientRect();
  const scaleX = canvas.width / rect.width;
  const scaleY = canvas.height / rect.height;
  return {
    x: (event.clientX - rect.left) * scaleX,
    y: (event.clientY - rect.top) * scaleY,
  };
}

function padWrapperStyle(palette: Palette): CSSProperties {
  return {
    position: 'relative',
    width: '100%',
    aspectRatio: `${PAD_WIDTH} / ${PAD_HEIGHT}`,
    border: `2px dashed ${palette.border}`,
    borderRadius: palette.radius,
    background: hexWithAlpha(palette.textMuted, 0.04),
    overflow: 'hidden',
  };
}

function canvasStyle(): CSSProperties {
  return {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    touchAction: 'none',
    cursor: 'crosshair',
  };
}

function hintStyle(palette: Palette): CSSProperties {
  return {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: hexWithAlpha(palette.textMuted, 0.6),
    fontSize: 14,
    pointerEvents: 'none',
  };
}

function clearButtonStyle(palette: Palette): CSSProperties {
  return {
    alignSelf: 'flex-end',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 12px',
    fontSize: 12,
    border: 'none',
    background: 'transparent',
    color: palette.textMuted,
    cursor: 'pointer',
  };
}

function EraserIcon({ color }: { color: string }): JSX.Element {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m7 21-4.3-4.3c-1-1-1-2.5 0-3.4l9.6-9.6c1-1 2.5-1 3.4 0l5.6 5.6c1 1 1 2.5 0 3.4L13 21" />
      <path d="M22 21H7" />
      <path d="m5 11 9 9" />
    </svg>
  );
}
